import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { useSocket } from '../context/SocketContext';
import api from '../services/api';
import { FiSend, FiSearch, FiPaperclip, FiMic, FiCheck, FiChevronLeft, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { formatTime, formatTime12, formatChatDateTime, formatLastMessageDate, formatDate, isSameDayIST } from '../utils/dateUtils';
import { getWhatsAppErrorMessage } from '../utils/formatUtils';

function LiveChat() {
  const [guests, setGuests] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showLatestButton, setShowLatestButton] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const socket = useSocket();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (container) {
      if (behavior === 'auto') {
        container.scrollTop = container.scrollHeight;
      } else {
        container.scrollTo({
          top: container.scrollHeight,
          behavior
        });
      }
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
    }
    setShowLatestButton(false);
  }, []);

  const fetchGuests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/chat/sessions');
      const guestsData = Array.isArray(response.data) ? response.data : [];
      setGuests(guestsData);
      setSelectedGuest((currentSelected) => {
        if (guestsData.length > 0 && !currentSelected) {
          return guestsData[0];
        }
        // If current selected guest is no longer in the list, clear selection
        if (currentSelected && !guestsData.find(g => 
          (g.id && g.id === currentSelected.id) || 
          (g.guest_id && g.guest_id === currentSelected.guest_id)
        )) {
          return null;
        }
        return currentSelected;
      });
    } catch (error) {
      console.error('Failed to fetch guests:', error);
      setGuests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);

  const loadChatHistory = useCallback(async (guestId) => {
    try {
      const response = await api.get(`/api/chat/history?guestId=${guestId}`);
      const history = Array.isArray(response.data) ? response.data : [];
      setMessages(history);
      // Guarantee instant scroll down to latest messages
      requestAnimationFrame(() => {
        scrollToBottom('auto');
      });
      setTimeout(() => {
        scrollToBottom('auto');
      }, 30);
      setTimeout(() => {
        scrollToBottom('auto');
      }, 100);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  }, [scrollToBottom]);

  const handleNewMessage = useCallback((message) => {
    setMessages((prev) => [...prev, message]);
    setTimeout(() => {
      scrollToBottom('smooth');
    }, 50);
  }, [scrollToBottom]);

  const handleMessageStatusUpdate = useCallback((data) => {
    if (!data || !data.messageId) return;
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.whatsapp_message_id === data.messageId) {
          return {
            ...msg,
            delivery_status: data.status,
            delivery_error: data.error || msg.delivery_error
          };
        }
        return msg;
      })
    );
  }, []);

  useEffect(() => {
    if (selectedGuest) {
      // Use guest_id if available, otherwise use id
      const guestId = selectedGuest.guest_id || selectedGuest.id;
      if (guestId) {
        loadChatHistory(guestId);
        if (socket) {
          socket.emit('join_guest_room', guestId);
          socket.on('new_message', handleNewMessage);
          socket.on('message_status_update', handleMessageStatusUpdate);
        }
      }
    }

    return () => {
      if (socket) {
        socket.off('new_message', handleNewMessage);
        socket.off('message_status_update', handleMessageStatusUpdate);
      }
    };
  }, [selectedGuest, socket, loadChatHistory, handleNewMessage, handleMessageStatusUpdate]);

  // Synchronous DOM layout scroll to bottom on every message or guest switch
  useLayoutEffect(() => {
    scrollToBottom('auto');
  }, [messages, selectedGuest, scrollToBottom]);

  // Secondary RAF and timeout triggers to guarantee scroll to latest messages after rendering
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      scrollToBottom('auto');
    });
    const timer1 = setTimeout(() => {
      scrollToBottom('auto');
    }, 50);
    const timer2 = setTimeout(() => {
      scrollToBottom('auto');
    }, 150);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [messages, selectedGuest, scrollToBottom]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedGuest || sending) return;

    try {
      setSending(true);
      setErrorMessage('');
      
      const guestId = selectedGuest.guest_id || selectedGuest.id;
      if (!guestId) {
        setErrorMessage('No guest ID available');
        return;
      }

      // Send message via WhatsApp API
      await api.post(`/api/admin/guests/${guestId}/send-message`, {
        message: newMessage.trim()
      });

      // Add message to local state immediately for better UX
      const tempMessage = {
        content: newMessage.trim(),
        direction: 'outbound',
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, tempMessage]);
      setNewMessage('');
      setTimeout(() => {
        scrollToBottom('smooth');
      }, 50);

      // Reload chat history to get the actual message from server
      setTimeout(() => {
        loadChatHistory(guestId);
      }, 500);
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Handle specific error codes
      let errorMsg = 'Failed to send message';
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.code === 'WHATSAPP_AUTH_ERROR' || errorData.code === 'WHATSAPP_NOT_CONFIGURED') {
          errorMsg = 'WhatsApp service is not properly configured. Please contact your administrator.';
        } else if (errorData.code === '24_HOUR_RULE') {
          errorMsg = 'Cannot send message due to WhatsApp 24-hour messaging rule. The guest needs to message you first.';
        } else {
          errorMsg = errorData.error || errorData.message || 'Failed to send message';
        }
        
        if (errorData.details && typeof errorData.details === 'string') {
          errorMsg += ` (${errorData.details})`;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setErrorMessage(errorMsg);
      // Clear error after 5 seconds
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setSending(false);
    }
  };

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowLatestButton(distanceFromBottom > 140);
  };

  // Always sorted by most recent conversation activity
  const filteredAndSortedGuests = useMemo(() => {
    return [...guests]
      .filter((guest) => {
        const name = guest.guest_name?.toLowerCase() || '';
        const room = guest.room_no?.toString() || '';
        const phone = guest.phone_number?.toString() || '';
        const query = searchQuery.toLowerCase().trim();
        if (!query) return true;
        return name.includes(query) || room.includes(query) || phone.includes(query);
      })
      .sort((a, b) => {
        const timeA = Math.max(
          a.last_message_time ? new Date(a.last_message_time).getTime() : 0,
          a.updated_at ? new Date(a.updated_at).getTime() : 0,
          a.created_at ? new Date(a.created_at).getTime() : 0
        );
        const timeB = Math.max(
          b.last_message_time ? new Date(b.last_message_time).getTime() : 0,
          b.updated_at ? new Date(b.updated_at).getTime() : 0,
          b.created_at ? new Date(b.created_at).getTime() : 0
        );
        return timeB - timeA;
      });
  }, [guests, searchQuery]);

  return (
    <Layout>
      <div className="h-full min-h-0 grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] fleet-card overflow-hidden">
        {/* Guest List */}
        <div className={`bg-white border-r border-gray-200 flex-col min-h-0 ${mobileShowChat ? 'hidden lg:flex' : 'flex'}`}>
          <div className="p-4 sm:p-6 border-b border-gray-200 space-y-3 sm:space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Guests</h2>
                <p className="text-xs text-gray-500 mt-0.5">Active conversations</p>
              </div>
              <button
                onClick={fetchGuests}
                className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
                title="Refresh"
              >
                Refresh
              </button>
            </div>
            <div className="relative">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search guests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-100 text-gray-700 rounded-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all border-none text-xs sm:text-sm"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-3"></div>
                <p className="text-sm text-gray-500">Loading guests...</p>
              </div>
            ) : filteredAndSortedGuests.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-gray-500 font-medium mb-1">No guests found</p>
                <p className="text-xs text-gray-400">Guests will appear here when they send messages via WhatsApp</p>
              </div>
            ) : (
              filteredAndSortedGuests.map((guest) => {
                const isSelected = selectedGuest?.id === guest.id || selectedGuest?.guest_id === guest.guest_id;
                return (
                  <div
                    key={guest.id || guest.guest_id}
                    onClick={() => {
                      setSelectedGuest(guest);
                      setMobileShowChat(true);
                    }}
                    className={`p-3.5 sm:p-4 cursor-pointer transition-all border-l-4 ${
                      isSelected 
                        ? 'bg-blue-50 border-blue-500 rounded-r-lg' 
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="flex justify-between items-baseline mb-0.5">
                          <p className={`font-semibold text-xs sm:text-sm truncate ${isSelected ? 'text-gray-900' : 'text-gray-900'}`}>
                            {guest.guest_name || 'Unknown Guest'}
                          </p>
                          {(guest.last_message_time || guest.updated_at || guest.created_at) && (
                            <span className="text-[11px] text-gray-500 font-medium whitespace-nowrap ml-2">
                              {formatLastMessageDate(guest.last_message_time || guest.updated_at || guest.created_at)}
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mb-1 font-medium ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                          {guest.phone_number ? `+ ${guest.phone_number}` : (guest.room_no ? `Room ${guest.room_no}` : 'No contact info')}
                        </p>
                        {guest.last_message && (
                          <p className={`text-xs truncate ${isSelected ? 'text-gray-600' : 'text-gray-500'}`}>
                            {guest.last_message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex-col bg-white min-h-0 relative ${!mobileShowChat ? 'hidden lg:flex' : 'flex'}`}>
          {selectedGuest ? (
            <>
              {/* Chat Header */}
              <div className="bg-white border-b border-gray-200 px-3.5 sm:px-6 py-3 sm:py-4 flex items-center gap-2.5 shadow-xs">
                <button
                  type="button"
                  onClick={() => setMobileShowChat(false)}
                  className="lg:hidden p-1.5 -ml-1 text-gray-700 hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center shrink-0"
                  title="Back to guests list"
                >
                  <FiChevronLeft size={22} />
                </button>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-0.5 flex items-center gap-2 truncate">
                    {selectedGuest.guest_name}
                  </h3>
                  <p className="text-xs text-gray-500 truncate">{selectedGuest.phone_number ? `+ ${selectedGuest.phone_number}` : (selectedGuest.room_no ? `Room ${selectedGuest.room_no}` : 'Not assigned')}</p>
                </div>
              </div>

              {/* Messages */}
              <div
                ref={messagesContainerRef}
                onScroll={handleMessagesScroll}
                className="flex-1 overflow-y-auto min-h-0 p-3.5 sm:p-6 bg-[#f8f9fa] space-y-4 sm:space-y-6"
              >
                {messages.map((message, index) => {
                  const isOutbound = message.direction === 'outbound';
                  const prevMessage = index > 0 ? messages[index - 1] : null;
                  const isDifferentDay = !prevMessage || !isSameDayIST(message.timestamp, prevMessage.timestamp);

                  return (
                    <React.Fragment key={message.id || index}>
                      {isDifferentDay && (
                        <div className="flex justify-center my-2">
                          <span className="bg-gray-200/80 text-gray-600 text-[11px] font-medium px-3 py-1 rounded-full shadow-2xs">
                            {formatDate(message.timestamp, 'EEEE, MMMM dd, yyyy')}
                          </span>
                        </div>
                      )}
                      <div
                        className={`flex flex-col ${
                          isOutbound ? 'items-end' : 'items-start'
                        }`}
                      >
                        {!isOutbound && (
                          <div className="flex items-center gap-2 mb-1 ml-1">
                            <span className="text-xs font-semibold text-gray-700">{selectedGuest.guest_name}</span>
                            <span className="text-[10px] text-gray-400 font-medium">{formatChatDateTime(message.timestamp)}</span>
                          </div>
                        )}
                        
                        <div
                          className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-4 sm:px-5 py-2.5 sm:py-3 shadow-xs ${
                            isOutbound
                              ? 'bg-[#d6eaff] text-gray-800 rounded-2xl rounded-tr-sm'
                              : 'bg-white text-gray-800 border border-gray-100 rounded-2xl rounded-tl-sm'
                          }`}
                        >
                          <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                        </div>

                        {isOutbound && (
                          <div className="flex items-center gap-1.5 mt-1 mr-1 text-[10px] text-gray-500">
                            <span>{formatChatDateTime(message.timestamp)}</span>
                            {message.delivery_status === 'failed' ? (
                              <span 
                                className="text-red-500 flex items-center gap-1 font-medium cursor-help"
                                title={getWhatsAppErrorMessage(message.delivery_error)}
                              >
                                <FiAlertCircle size={11} className="text-red-500" />
                                <span>Failed {message.delivery_error?.code ? `(${message.delivery_error.code})` : ''}</span>
                              </span>
                            ) : message.delivery_status === 'read' ? (
                              <span title="Read by guest on WhatsApp" className="flex items-center gap-0.5 text-blue-500 font-medium">
                                <FiCheckCircle size={11} />
                                <span>Read</span>
                              </span>
                            ) : message.delivery_status === 'delivered' ? (
                              <span title="Delivered to guest WhatsApp device" className="flex items-center gap-0.5 text-green-600">
                                <FiCheckCircle size={11} />
                                <span>Delivered</span>
                              </span>
                            ) : (
                              <span title="Sent to WhatsApp, waiting for delivery confirmation" className="flex items-center gap-0.5 text-gray-400">
                                <FiCheck size={11} />
                                <span>Sent</span>
                              </span>
                            )}
                            <span>via WhatsApp</span>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {showLatestButton && (
                <button
                  onClick={() => scrollToBottom('smooth')}
                  className="absolute bottom-20 right-4 sm:bottom-24 sm:right-6 z-20 px-3 py-1.5 sm:py-2 rounded-full bg-gray-900 text-white text-xs font-semibold shadow-lg hover:bg-gray-800 transition-colors"
                  title="Jump to latest"
                >
                  Latest ↓
                </button>
              )}

              {/* Message Input */}
              <div className="bg-[#f8f9fa] px-3 sm:px-6 py-3 pb-4 sm:pb-6">
                {errorMessage && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{errorMessage}</p>
                  </div>
                )}
                <div className="flex items-center gap-2 sm:gap-3 bg-white p-1.5 sm:p-2 pr-1.5 sm:pr-2 pl-3 sm:pl-4 rounded-full shadow-sm border border-gray-100">
                  <button className="text-gray-400 hover:text-gray-600 p-1.5 sm:p-2 transition-colors rounded-full hover:bg-gray-50" title="Attach file (coming soon)">
                    <FiPaperclip size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </button>
                  <button className="text-gray-400 hover:text-gray-600 p-1.5 sm:p-2 transition-colors rounded-full hover:bg-gray-50" title="Voice message (coming soon)">
                    <FiMic size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </button>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      setErrorMessage(''); // Clear error when typing
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !sending) {
                        sendMessage();
                      }
                    }}
                    placeholder="Type your message..."
                    disabled={sending}
                    className="flex-1 bg-transparent border-none focus:outline-none focus:ring-0 text-xs sm:text-sm text-gray-700 placeholder-gray-400 px-1 sm:px-2 min-w-0"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="bg-[#9c6fe4] text-white p-2.5 sm:p-3 rounded-full hover:bg-[#8b5bd4] focus:outline-none focus:ring-2 focus:ring-[#9c6fe4] focus:ring-offset-2 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
                  >
                    {sending ? (
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                    ) : (
                      <FiSend size={16} className="sm:w-[18px] sm:h-[18px] translate-x-[1px] translate-y-[1px]" />
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-6">
                <p className="text-gray-400 text-sm font-medium mb-1">No guest selected</p>
                <p className="text-gray-300 text-xs">Select a guest from the list to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default LiveChat;
